/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Features section for AI Image Enhancer - showcasing key capabilities
 */
import Image from 'next/image';
import React from 'react';

import { scrollToTop } from '@/utils/landing/scrollToTop';

interface FeatureItem {
  title: string;
  description: string;
  imageUrl: string;
}

const features: FeatureItem[] = [
  {
    title: 'Enhance Your Photos with AI',
    description:
      "Our AI-powered photo enhancer gives your images an instant upgrade, turning everyday shots into crisp, high-quality visuals. It analyzes lighting, texture, and detail to clean up dull or unclear photos automatically. E-commerce sellers can make product listings look more trustworthy, creators can polish social content without extra tools, and marketing teams can prepare cleaner visuals for ads or banners. It's a simple way to improve any image with just one upload.",
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/product-device-image.webp',
  },
  {
    title: 'Make Any Image Look Bigger and Sharper',
    description:
      'Enlarge your images to 2K, 4K, or 8K while rebuilding the fine details that usually get lost when you stretch a photo. Instead of blowing up pixels, FalcoCut rebuilds edges, textures, and patterns so your visuals stay crisp at larger sizes. Tiny images can be expanded for clearer print materials, digital graphics can gain sharper edges for interface previews, and personal or travel photos can become clean, high-resolution visuals for presentations, blogs, or side-by-side comparisons.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/outdoor-speaker-image.webp',
  },
  {
    title: 'Upgrade 10 Images in One Go',
    description:
      'Dealing with a pile of photos? Enhance them all at once. Our AI batch enhancer lets you upload up to 10 images at the same time, giving every photo a clean, consistent boost. The results appear almost instantly—no slow queues or repetitive editing. From product galleries to UI mockups to lifestyle snapshots, everything gets refreshed together. The AI sharpens details, balances tones, and cleans up imperfections so your visuals look polished and ready to use.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/batch-processing-image.webp',
  },
  {
    title: 'Natural Color & Contrast Adjustment',
    description:
      "Brighten lifeless images instantly and bring their colors back to life. Our AI color enhancer boosts brightness, contrast, and tone in one click, giving every photo a more vivid, natural look without harsh edits. You'll see richer colors, cleaner highlights, and balanced shadows across all kinds of photos—family moments, outdoor adventures, pet pictures, or creative hobby shots. Each picture shines with gentle improvements that make it ready for any moment you want to share.",
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/beauty-creator-image.webp',
  },
  {
    title: 'Fix Blurry Photos Fast',
    description:
      "Soft or unfocused photos don't have to stay that way. FalcoCut's unblur tool tightens edges, restores lost detail, and brings each image back into clear focus with a single click. You'll see the difference right away—cleaner lines, brighter textures, and a more defined look overall. Whether it's portraits, scenery, close-ups, or action shots, every photo gets a smooth boost in clarity. The result is a sharper, truer image that fits smoothly into any project or design.",
    imageUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/skincare-still-life-image.webp',
  },
  {
    title: 'Remove Noise & Grain',
    description:
      'Grainy photos can lose their natural look, especially in low light. With FalcoCut, unwanted grain is softened and your image returns to a balanced, even texture. Details stay clear while the background becomes calmer and easier on the eyes. From product shots and lifestyle images to branded visuals and social content, every photo looks smoother and more balanced. The final result delivers a clean, refined finish that lifts your visuals to a studio-ready standard.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/mop-product-demo-image.webp',
  },
  {
    title: 'Adaptable to Every Photo You Take',
    description:
      'FalcoCut adjusts to whatever you throw at it. Selfies, product shots, screenshots, social posts—each one gets the right kind of boost without looking over-edited. The enhancer reads the image, improves what matters, and keeps everything feeling natural and true to the source. Soft areas become clearer, textures stay smooth, and the whole photo gains a clean, consistent finish. No matter the format or where the image came from, FalcoCut helps your visuals look polished and ready for your next creative move.',
    imageUrl: 'https://assets.falcocut.ai/ai-image-enhancer/sports-gear-image.webp',
  },
  {
    title: 'One Platform for All Your Visual Needs',
    description:
      "FalcoCut isn't just an image enhancer—it's a complete creative studio. You can create images, upscale photos, refine details, and make quick edits without switching between different apps. Everything works together smoothly, giving you a simple setup for handling both single images and batch projects. This streamlined approach cuts down on busywork and keeps your workflow clear, so you can focus on the results that matter most.",
    imageUrl:
      'https://assets.falcocut.ai/ai-image-enhancer/creator-presentation-image.webp',
  },
];

// 箭头图标
const ArrowRightIcon: React.FC = () => (
  <svg viewBox="64 64 896 896" fill="currentColor" width="1em" height="1em">
    <path d="M869 487.8L491.2 159.9c-2.9-2.5-6.6-3.9-10.5-3.9h-88.5c-7.4 0-10.8 9.2-5.2 14l350.2 304H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h585.1L386.9 854c-5.6 4.9-2.2 14 5.2 14h91.5c1.9 0 3.8-.7 5.2-2L869 536.2a32.07 32.07 0 000-48.4z" />
  </svg>
);

interface FeatureCardProps {
  feature: FeatureItem;
  isReversed: boolean;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ feature, isReversed }) => {
  return (
    <div
      className={`flex flex-col ${isReversed ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-8 pad:gap-10 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden bg-gray-100">
          <Image
            src={feature.imageUrl}
            alt={feature.title}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full">
        <h3 className="text-xl pad:text-2xl font-bold text-gray-900 mb-4">
          {feature.title}
        </h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed mb-6">
          {feature.description}
        </p>
        <button
          type="button"
          onClick={() => scrollToTop()}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-lg"
          style={{ background: '#745CFF' }}
        >
          <span>Enhance Your Image</span>
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
};

const FeaturesSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Header */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-4">
          AI Image Enhancement for Sharper, Brighter Results
        </h2>
        <p className="text-sm pad:text-base text-gray-600 text-center max-w-4xl mx-auto mb-12 pad:mb-16">
          FalcoCut AI Image Enhancer is built to make photo improvement simple and
          effective. From resolution upgrades to color correction, it gives you the tools
          to fix common problems and bring out the best in any picture—all in one easy
          platform.
        </p>

        {/* Features List */}
        <div className="space-y-16 pad:space-y-20">
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              feature={feature}
              isReversed={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
